/**
 * The projector's tuning for the wall, set on the calibration screen and applied by the projector.
 * The look (palette and accent) is the engine's Look, saved on the tournament for every screen;
 * the brightness and contrast filter belongs to this browser and is kept in localStorage.
 */

import type { Look } from "../../../../engine/types";

export type { Look };

export interface Tuning {
  /** Percent, 100 = unchanged. */
  brightness: number;
  contrast: number;
}

const KEY = "bd.cal";

export const DEFAULT_TUNING: Tuning = { brightness: 100, contrast: 100 };

export function loadTuning(): Tuning {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_TUNING;
    const t = JSON.parse(raw) as Partial<Tuning>;
    return {
      brightness: typeof t.brightness === "number" ? t.brightness : 100,
      contrast: typeof t.contrast === "number" ? t.contrast : 100,
    };
  } catch {
    return DEFAULT_TUNING;
  }
}

export function saveTuning(t: Tuning): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(t));
  } catch {
    /* private window: the tuning lasts until the page closes */
  }
}

/**
 * The CSS filter for the view's root element. None at all when nothing is tuned: a filter on the
 * root, even one that changes nothing, makes the browser redraw the whole wall through it on
 * every frame of every animation.
 */
export function filterOf(t: Tuning): string | undefined {
  if (t.brightness === 100 && t.contrast === 100) return undefined;
  return `brightness(${t.brightness}%) contrast(${t.contrast}%)`;
}

/** The message that saves a look for everyone. The projector role may send exactly this. */
export function lookEvent(look: Look): { type: "config.updated"; patch: { look: Look } } {
  return { type: "config.updated", patch: { look } };
}
